import React from "react";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  KeyRound,
  MapPin,
  Phone,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import AddressLink from "../../../../components/ModalConsult/AddressLink";
import PropertyFamilyFields from "../../../../components/ModalConsult/PropertyFamilyFields";
import VisitDigitalKeyPassword from "../../../../components/ModalConsult/VisitDigitalKeyPassword";
import { Guarantee } from "../../../../global/types";
import { formatVisitPropertyAddress } from "../../../../utils/visitDetailsProperty";
import {
  resolveVisitDigitalKeyPassword,
  resolveVisitKeyLocationName,
} from "../../../../utils/visitKeyLocation";
import { buildWhatsAppLink } from "../../../../utils/whatsappLink";
import type { ScheduleDetailAppointment } from "./useScheduleDetailAppointments";

type ClientPagination = {
  currentIndex: number;
  total: number;
  onPrevious: () => void;
  onNext: () => void;
};

type ScheduleDetailPanelBodyProps = {
  appointment: ScheduleDetailAppointment;
  canManageSchedules: boolean;
  clientPagination?: ClientPagination | null;
};

function formatGuaranteesList(guarantees: Guarantee[] = []) {
  if (guarantees.length === 0) {
    return "O imóvel não possui garantias cadastradas";
  }
  return guarantees.map((item) => item.name).join(", ");
}

function formatScheduleHeaderDate(raw: string) {
  const match = String(raw).match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/
  );
  if (!match) {
    return raw;
  }
  const [, year, month, day, hour, minute] = match;
  return `${day}/${month}/${year} · ${hour}:${minute}`;
}

function SectionTitle({
  title,
  trailing,
}: {
  title: string;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex items-center justify-between gap-2">
      <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-[#4A5568]">
        {title}
      </h3>
      {trailing}
    </div>
  );
}

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3 border-t border-[#EDF2F7] pt-3 first:border-t-0 first:pt-0">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#94A3B8]" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
          {label}
        </p>
        <div className="mt-1 text-sm leading-relaxed text-[#334155]">{children}</div>
      </div>
    </div>
  );
}

function ContactActions({ phone }: { phone?: string | null }) {
  const whatsapp = buildWhatsAppLink(phone);
  if (!phone && !whatsapp) {
    return null;
  }
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {whatsapp ? (
        <a
          href={whatsapp}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800"
        >
          WhatsApp
        </a>
      ) : null}
      {phone ? (
        <a
          href={`tel:${phone}`}
          className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700"
        >
          <Phone className="h-3.5 w-3.5" aria-hidden />
          Ligar
        </a>
      ) : null}
    </div>
  );
}

function ClientPaginationControls({
  pagination,
}: {
  pagination: ClientPagination;
}) {
  if (pagination.total <= 1) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 text-xs font-semibold text-[#718096]">
      <button
        type="button"
        className="rounded-md p-1 hover:bg-[#F7FAFC]"
        onClick={pagination.onPrevious}
        aria-label="Cliente anterior"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
      </button>
      <span>
        {pagination.currentIndex + 1}/{pagination.total}
      </span>
      <button
        type="button"
        className="rounded-md p-1 hover:bg-[#F7FAFC]"
        onClick={pagination.onNext}
        aria-label="Próximo cliente"
      >
        <ChevronRight className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

const ScheduleDetailPanelBody: React.FC<ScheduleDetailPanelBodyProps> = ({
  appointment,
  canManageSchedules,
  clientPagination,
}) => {
  const property = appointment.property ?? {};
  const client = appointment.client ?? {};
  const owner = appointment.owner ?? {};
  const digitalKeyPassword = resolveVisitDigitalKeyPassword(property);
  const guarantees = (property.guarantee as Guarantee[] | undefined) ?? [];

  return (
    <div className="space-y-4 pb-2">
      <section className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
        <SectionTitle title="Imóvel" />
        {property.photo ? (
          <img
            src={String(property.photo)}
            alt="Foto do imóvel"
            className="mb-4 aspect-[16/10] w-full rounded-lg object-cover"
          />
        ) : null}
        <div className="space-y-4 text-sm text-neutral-700">
          <p className="flex flex-wrap items-center gap-1 text-base font-bold text-[#1E293B]">
            {property.title ? String(property.title) : "Imóvel"}
            {property.id ? (
              <a
                href={`https://www.7cantos.com/imovel/${property.id}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 text-[#c4552b]"
              >
                #{String(property.id)}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            ) : null}
          </p>
          <div className="space-y-3">
            <DetailRow icon={MapPin} label="Endereço">
              {formatVisitPropertyAddress(property)}
            </DetailRow>
            <DetailRow icon={KeyRound} label="Chaves">
              {resolveVisitKeyLocationName(property, appointment.key_location)}
            </DetailRow>
            {digitalKeyPassword ? (
              <VisitDigitalKeyPassword password={digitalKeyPassword} />
            ) : null}
            <DetailRow icon={ShieldCheck} label="Garantias">
              {formatGuaranteesList(guarantees)}
            </DetailRow>
            <DetailRow icon={ShieldCheck} label="Administração">
              {property.is_adm ? "Administrado 7Cantos" : "Não administrado"}
            </DetailRow>
          </div>
          {property.note ? (
            <p className="rounded-lg bg-neutral-50 p-3 text-xs leading-relaxed">
              {String(property.note)}
            </p>
          ) : null}
          <PropertyFamilyFields property={property} />
        </div>
      </section>

      <section className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
        <SectionTitle title="Agendamento" />
        <div className="space-y-3 text-sm text-neutral-700">
          <DetailRow icon={CalendarDays} label="Data e horário">
            {formatScheduleHeaderDate(appointment.schedule.date)}
          </DetailRow>
          <DetailRow icon={MapPin} label="Tipo e endereço">
            <span className="font-medium text-[#1E293B]">
              {appointment.video_call ? "Vídeo" : "Presencial"}
            </span>
            {!appointment.video_call ? (
              <>
                <span className="mx-1 text-[#CBD5E1]">·</span>
                <AddressLink
                  address={formatVisitPropertyAddress(property)}
                  service="waze"
                  className="font-medium text-[#c4552b] underline underline-offset-2 hover:text-[#a84424]"
                />
              </>
            ) : null}
          </DetailRow>
          <DetailRow icon={UserRound} label="Consultor">
            {appointment.consultant.first_name} · {appointment.consultant.phone}
          </DetailRow>
          {appointment.note || appointment.schedule.note ? (
            <p className="rounded-lg bg-[#F8FAFC] p-3 text-xs leading-relaxed text-[#475569]">
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#94A3B8]">
                Observação da agenda
              </span>
              {appointment.note ?? appointment.schedule.note}
            </p>
          ) : null}
        </div>
      </section>

      <section className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
        <SectionTitle
          title="Cliente"
          trailing={
            clientPagination ? (
              <ClientPaginationControls pagination={clientPagination} />
            ) : null
          }
        />
        <div className="text-sm text-neutral-700">
          <p className="font-semibold text-neutral-900">
            {String(client.first_name ?? "Cliente não informado")}
          </p>
          <p className="mt-1 text-xs text-neutral-500">
            {String(client.email ?? "E-mail não informado")}
          </p>
          <p className="text-xs text-neutral-500">
            {String(client.phone ?? "Telefone não informado")}
          </p>
          {canManageSchedules ? (
            <ContactActions phone={client.phone as string | undefined} />
          ) : null}
        </div>
      </section>

      <section className="rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm">
        <SectionTitle title="Proprietário" />
        <div className="text-sm text-neutral-700">
          <p className="font-semibold text-neutral-900">
            {String(owner.first_name ?? "Proprietário não informado")}
          </p>
          <p className="mt-1 text-xs text-neutral-500">
            {String(owner.email ?? "E-mail não informado")}
          </p>
          <p className="text-xs text-neutral-500">
            {String(owner.phone ?? "Telefone não informado")}
          </p>
          {canManageSchedules ? (
            <ContactActions phone={owner.phone as string | undefined} />
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default ScheduleDetailPanelBody;
